import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Drawer,
  Form,
  Grid,
  Input,
  InputNumber,
  Select,
  DatePicker,
  Button,
  Modal,
  message,
} from "antd";
import { CheckOutlined } from "@ant-design/icons";
import dayjs from "dayjs";
import {
  createHostRentContract,
  updateHostRentContract,
  getLastMeterReadingRecord,
} from "../../api/host";
import HostSelect from "./HostSelect";
import UserSelect from "../user/UserSelect";
import { toBjNoon, fromBj, fmtBj, today } from "../../utils/bjDate";
import { CONTRACT_STATUS_MAP } from "./rentBillConstants";

const FMT = "YYYY/MM/DD";
const CYCLE_OPTIONS = [
  { value: "daily", label: "日付" },
  { value: "monthly", label: "月付" },
  { value: "quarterly", label: "季付" },
  { value: "yearly", label: "年付" },
];
const UNIT = { daily: "天", monthly: "月", quarterly: "季", yearly: "年" };
const n2 = (v) =>
  v == null
    ? "-"
    : Number(v).toLocaleString("zh-CN", { maximumFractionDigits: 2 });
const idOf = (v) => (v && typeof v === "object" ? v._id : v); // populated 对象或 id 都兼容
const errMsg = (e, fallback) =>
  e?.response?.data?.msg ||
  e?.response?.data?.message ||
  e?.response?.data?.error ||
  fallback;
const readLoginUser = () => {
  try {
    return JSON.parse(localStorage.getItem("user") || "null") || undefined;
  } catch {
    return undefined;
  }
};

const css = `
.rcf{max-width:560px;margin:0 auto;}
.rcf-row{display:flex;align-items:flex-start;gap:8px;margin-bottom:10px;}
.rcf-label{flex:0 0 4.4em;width:4.4em;line-height:32px;font-size:13px;color:#64748b;white-space:nowrap;overflow:hidden;transition:flex-basis .2s,width .2s,opacity .2s;}
.rcf-row:focus-within>.rcf-label{flex-basis:0;width:0;opacity:0;}
.rcf-cell{min-width:0;}
.rcf .ant-select{width:100%;}
.rcf-chip{width:100%;height:32px;padding:0 10px;border-radius:8px;border:1px solid #d9d9d9;background:#fff;color:#64748b;font:inherit;font-size:13px;
  display:inline-flex;align-items:center;justify-content:center;gap:4px;white-space:nowrap;cursor:pointer;-webkit-tap-highlight-color:transparent;}
.rcf-chip.on{border-color:#2563eb;background:#eff6ff;color:#1d4ed8;}
.rcf-ro{display:flex;align-items:center;height:32px;padding:0 11px;border-radius:6px;background:rgba(148,163,184,.12);color:#64748b;font-size:13px;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;}
.rcf-ro b{margin-left:4px;color:#0f172a;font-weight:600;}
.rcf-field{display:flex;align-items:flex-start;gap:6px;}
.rcf-flabel{flex:0 0 4.4em;width:4.4em;line-height:32px;font-size:13px;color:#64748b;white-space:nowrap;overflow:hidden;transition:flex-basis .2s,width .2s,opacity .2s;}
.rcf-field:focus-within>.rcf-flabel{flex-basis:0;width:0;opacity:0;}
.rcf-fctl{flex:1;min-width:0;}
.rcf-sfx{color:#94a3b8;font-size:12px;white-space:nowrap;}
.rcf-people{display:flex;gap:8px;margin-bottom:10px;}
.rcf-pcell{flex:1 1 0;min-width:0;}
.rcf-pcell:focus-within{flex-grow:2;} /* 聚焦的人员框变宽；不加过渡，保证下拉框按展开后的宽度弹出 */
.rcf-u{position:relative;}
.rcf-u .ant-select-selection-item{padding-inline-end:var(--tw,46px) !important;}
.rcf-utag{position:absolute;right:30px;top:50%;transform:translateY(-50%);z-index:1;padding:0 2px 0 6px;background:#fff;color:#94a3b8;font-size:12px;line-height:20px;pointer-events:none;}
.rcf-divider{height:1px;margin:2px 16px 12px;background:rgba(148,163,184,.35);}
.rcf-chip[disabled]{opacity:.6;cursor:not-allowed;}
.rcf-foot{display:flex;justify-content:flex-end;gap:8px;max-width:560px;margin:0 auto;}
`;

// 二态开关按钮（配合 Form.Item）：房东承担(on) / 客户承担(off)
function SideChip({ value, onChange, name, disabled }) {
  return (
    <button
      type="button"
      className={`rcf-chip${value ? " on" : ""}`}
      aria-pressed={!!value}
      disabled={disabled}
      onClick={() => onChange?.(!value)}
    >
      {value && <CheckOutlined />}
      {name} {value ? "房东承担" : "客户承担"}
    </button>
  );
}

const Row = ({ label, children }) => (
  <div className="rcf-row">
    {label && <label className="rcf-label">{label}</label>}
    {children}
  </div>
);
const Cell = ({ flex = 1, f, children }) => (
  <div className="rcf-cell" style={{ flex }} data-f={f}>
    {children}
  </div>
);
// 带左侧标题的单个字段（聚焦时标题隐藏）
const Field = ({ label, children }) => (
  <div className="rcf-field">
    <label className="rcf-flabel">{label}</label>
    <div className="rcf-fctl">{children}</div>
  </div>
);
// 用户选择框：在框内尾部标记身份（客户/签约/跟进）
const UserField = ({ tag, wide, f, children }) => (
  <div className="rcf-u" style={{ "--tw": wide ? "64px" : "46px" }} data-f={f}>
    <span className="rcf-utag">{tag}</span>
    {children}
  </div>
);
// 日期框：面板只在点击时打开（聚焦不弹出）；选完日期后触发 onPicked（用于跳到下一项）
function DateField({ value, onChange, onPicked, placeholder, tag, clearable }) {
  const [open, setOpen] = useState(false);
  return (
    <div onClick={(e) => e.currentTarget.contains(e.target) && setOpen(true)}>
      <DatePicker
        value={value}
        open={open}
        onOpenChange={(o) => !o && setOpen(false)}
        onChange={(d, ds) => {
          onChange?.(d, ds);
          if (d) onPicked?.();
        }}
        placeholder={placeholder}
        format={FMT}
        inputReadOnly
        allowClear={clearable}
        suffixIcon={<span className="rcf-sfx">{tag}</span>}
        style={{ width: "100%" }}
      />
    </div>
  );
}
// 输入焦点顺序（新增时：房间 -> 客户 -> 开始 -> 结束 -> 付款类型 -> 周期 -> 租金 -> 预付 -> 押金 -> 水表 -> 电表 -> 备注）
const FIELD_ORDER = [
  "host",
  "client",
  "contractStartDate",
  "contractExpiryDate",
  "rentPaymentCycle",
  "rentPaymentCycleMultiplier",
  "rent",
  "prePaidDays",
  "securityDeposit",
  "waterMeterStart",
  "electricityMeterStart",
  "notes",
];
const clamp12 = (v) => Math.min(2, Math.max(1, Number(v) || 1)); // 水电、物业结算周期：1~2 个月
const Item = (props) => <Form.Item style={{ marginBottom: 0 }} {...props} />;
const req = (m) => [{ required: true, message: m }];

/**
 * 合约抽屉：新增(contract 为空) / 编辑(contract 有值)
 * 编辑时：房间锁定；状态、租金有效日期、实付押金只读，且不随保存提交
 * props 与原 HostRentContractModal 一致：open / contract / onCancel / onSuccess(newContract)
 */
export default function RentContractDrawer({
  open,
  contract,
  onCancel,
  onSuccess,
}) {
  const [form] = Form.useForm();
  const isEdit = !!contract?._id;
  const [saving, setSaving] = useState(false);
  const cycle = Form.useWatch("rentPaymentCycle", form);
  const freeWE = Form.useWatch("freeWaterElectricity", form);
  const freePF = Form.useWatch("freePropertyFee", form);
  const unit = UNIT[cycle] || ""; // 付款周期的单位
  const wide = Grid.useBreakpoint().sm; // 窄屏(手机)用单字标记
  const me = useMemo(() => readLoginUser(), [open]); // 当前登录用户（新增时作为签约人、跟进人的默认值）

  useEffect(() => {
    if (!open) {
      form.resetFields();
      return;
    }
    if (isEdit) {
      const c = contract;
      form.setFieldsValue({
        host: idOf(c.host),
        client: idOf(c.client),
        signer: idOf(c.signer),
        followUpPerson: idOf(c.followUpPerson),
        contractSignDate: fromBj(c.contractSignDate),
        contractStartDate: fromBj(c.contractStartDate),
        contractExpiryDate: fromBj(c.contractExpiryDate),
        rentPaymentCycle: c.rentPaymentCycle,
        rentPaymentCycleMultiplier: c.rentPaymentCycleMultiplier,
        rent: c.rent,
        prePaidDays: c.prePaidDays,
        securityDeposit: c.securityDeposit,
        freeWaterElectricity: !!c.freeWaterElectricity,
        waterElectricityPaymentCycleMultiplier: clamp12(
          c.waterElectricityPaymentCycleMultiplier,
        ), // 旧数据可能超出 1~2，打开时收敛
        waterMeterStart: c.waterMeterStart,
        electricityMeterStart: c.electricityMeterStart,
        freePropertyFee:
          c.rentPaymentCycle === "daily" ? true : !!c.freePropertyFee, // 日租只能包物业
        propertyFeePaymentCycleMultiplier: clamp12(
          c.propertyFeePaymentCycleMultiplier,
        ),
        notes: c.notes,
      });
    } else {
      form.setFieldsValue({
        signer: me?._id, // 默认当前登录用户
        followUpPerson: me?._id,
        contractSignDate: today(),
        contractStartDate: today(),
        prePaidDays: 7,
        rentPaymentCycle: "monthly",
        rentPaymentCycleMultiplier: 1,
        freeWaterElectricity: false,
        waterElectricityPaymentCycleMultiplier: 1,
        freePropertyFee: true,
        propertyFeePaymentCycleMultiplier: 1,
      });
      const t = setTimeout(() => {
        const input = rootRef.current?.querySelector('[data-f="host"] input');
        input?.setAttribute("inputmode", "numeric"); // 房间号搜索以数字为主，弹数字键盘
        input?.setAttribute("enterkeyhint", "next");
        input?.focus();
      }, 400); // 等抽屉动画结束
      return () => clearTimeout(t);
    }
  }, [open, contract?._id]);

  // 新增时选择房间后，用最近 5 天内的抄表记录预填水表、电表底数（只在用户选房间时触发，编辑不会覆盖已存的底数）
  const prefillMeter = async (host) => {
    try {
      const last = (await getLastMeterReadingRecord({ host })).data;
      if (
        last &&
        dayjs().diff(dayjs(last.readingTime), "day") < 5 &&
        form.getFieldValue("host") === host
      ) {
        form.setFieldsValue({
          waterMeterStart: last.waterReading,
          electricityMeterStart: last.electricReading,
        });
      }
    } catch {
      /* 预填失败不影响录入 */
    }
  };

  const onValuesChange = (changed) => {
    if (changed.rentPaymentCycle === "daily")
      form.setFieldsValue({ freePropertyFee: true }); // 日租只能包物业
    if (isEdit) return;
    // 开始日期跟随签约日期，直到用户自己改过开始日期
    if (
      "contractSignDate" in changed &&
      !form.isFieldTouched("contractStartDate")
    ) {
      form.setFieldsValue({ contractStartDate: changed.contractSignDate });
    }
    if (changed.host) prefillMeter(changed.host);
  };

  const close = () => onCancel?.();
  const requestClose = () => {
    if (saving) return;
    if (form.isFieldsTouched()) {
      Modal.confirm({
        title: "放弃未保存的修改？",
        okText: "放弃",
        cancelText: "继续编辑",
        onOk: close,
      });
    } else close();
  };

  const handleSave = async () => {
    let v;
    try {
      v = await form.validateFields();
    } catch {
      return;
    }
    // 只提交可编辑字段：房间、状态、租金有效日期、实付押金不提交；
    // 被隐藏的字段(如房东承担水电时的水表底数)不在 v 里，因此不会被改动
    const payload = {
      client: v.client,
      signer: v.signer,
      followUpPerson: v.followUpPerson,
      contractSignDate: toBjNoon(v.contractSignDate),
      contractStartDate: toBjNoon(v.contractStartDate),
      contractExpiryDate: v.contractExpiryDate
        ? toBjNoon(v.contractExpiryDate)
        : null,
      rentPaymentCycle: v.rentPaymentCycle,
      rentPaymentCycleMultiplier: v.rentPaymentCycleMultiplier,
      rent: v.rent,
      prePaidDays: v.prePaidDays,
      securityDeposit: v.securityDeposit,
      freeWaterElectricity: !!v.freeWaterElectricity,
      waterElectricityPaymentCycleMultiplier:
        v.waterElectricityPaymentCycleMultiplier,
      waterMeterStart: v.waterMeterStart,
      electricityMeterStart: v.electricityMeterStart,
      freePropertyFee:
        v.rentPaymentCycle === "daily" ? true : !!v.freePropertyFee,
      propertyFeePaymentCycleMultiplier: v.propertyFeePaymentCycleMultiplier,
      notes: v.notes,
    };
    setSaving(true);
    try {
      if (isEdit) {
        const res = await updateHostRentContract(contract._id, payload);
        message.success("保存成功");
        onSuccess?.(res.data);
      } else {
        const res = await createHostRentContract({
          ...payload,
          host: v.host,
          rentValidDate: toBjNoon(v.contractStartDate.subtract(1, "day")), // 租金有效日期默认为开始日期前一天
          active: true,
        });
        message.success("新增成功");
        onSuccess?.(res.data);
      }
    } catch (e) {
      message.error(errMsg(e, isEdit ? "保存合约失败" : "新增合约失败"));
    } finally {
      setSaving(false);
    }
  };

  const dp = (ph, tag, clearable = false, onPicked) => (
    <DateField
      placeholder={ph}
      tag={tag}
      clearable={clearable}
      onPicked={onPicked}
    />
  );
  const rootRef = useRef(null);
  // 跳到顺序中的下一个存在且可用的字段（被隐藏或禁用的会跳过）
  const goNext = (from) => {
    const root = rootRef.current;
    if (!root) return;
    for (let i = FIELD_ORDER.indexOf(from) + 1; i < FIELD_ORDER.length; i++) {
      const name = FIELD_ORDER[i];
      const el = root.querySelector(
        `[data-f="${name}"] input:not([disabled]), [data-f="${name}"] textarea`,
      );
      if (el) {
        setTimeout(() => el.focus(), 0); // 等选择框收起后再聚焦
        return;
      }
    }
  };
  // 回车 / 输入法"下一项"：按上面的顺序跳转（输入法选字中的回车不算）
  const onKeyDown = (e) => {
    if (e.key !== "Enter" || e.nativeEvent?.isComposing || e.keyCode === 229)
      return;
    if (e.target.tagName === "TEXTAREA") return;
    const holder = e.target.closest?.("[data-f]");
    if (holder) {
      e.preventDefault();
      goNext(holder.dataset.f);
    }
  };
  const num = (props) => (
    <InputNumber
      controls={false}
      inputMode="decimal"
      style={{ width: "100%" }}
      {...props}
    />
  );
  const statusCfg = CONTRACT_STATUS_MAP[contract?.status] || {
    label: contract?.status,
    color: "default",
  };

  return (
    <Drawer
      placement="top"
      open={open}
      onClose={requestClose}
      closable={false}
      forceRender
      height="auto"
      styles={{
        wrapper: { maxHeight: "92vh" },
        body: {
          padding: "calc(12px + env(safe-area-inset-top, 0px)) 12px 4px",
        },
        footer: {
          padding: "8px 12px calc(8px + env(safe-area-inset-bottom, 0px))",
        },
      }}
      footer={
        <div className="rcf-foot">
          <Button onClick={requestClose} disabled={saving}>
            取消
          </Button>
          <Button type="primary" onClick={handleSave} loading={saving}>
            {isEdit ? "保存" : "创建合约"}
          </Button>
        </div>
      }
    >
      <style>{css}</style>
      <div className="rcf" ref={rootRef} onKeyDown={onKeyDown}>
        <Form form={form} component="div" onValuesChange={onValuesChange}>
          {isEdit && (
            <Row>
              <Cell>
                <div className="rcf-ro">
                  状态
                  <b
                    style={{
                      color:
                        statusCfg.color === "success" ? "#15803d" : undefined,
                    }}
                  >
                    {statusCfg.label}
                  </b>
                </div>
              </Cell>
              <Cell>
                <div className="rcf-ro">
                  租金有效至<b>{fmtBj(contract.rentValidDate, FMT)}</b>
                </div>
              </Cell>
            </Row>
          )}
          <Row>
            <Cell f="host">
              <Item name="host" rules={req("请选择房间")}>
                <HostSelect
                  disabled={isEdit}
                  filters={{ status: "empty", valid: true }}
                  placeholder="房间"
                  onChange={() => goNext("host")}
                />
              </Item>
            </Cell>
            <Cell>
              <Item name="contractSignDate" rules={req("签约日期")}>
                {dp("签约日期", "签约")}
              </Item>
            </Cell>
          </Row>
          <div className="rcf-people">
            <div className="rcf-pcell">
              <UserField tag={wide ? "客户" : "客"} wide={wide} f="client">
                <Item name="client" rules={req("请选择客户")}>
                  <UserSelect
                    allowCreate={!contract?.client?._id}
                    placeholder="客户"
                    onChange={() => goNext("client")}
                  />
                </Item>
              </UserField>
            </div>
            <div className="rcf-pcell">
              <UserField tag={wide ? "签约" : "签"} wide={wide}>
                <Item name="signer">
                  <UserSelect allowCreate={false} placeholder="签约人" />
                </Item>
              </UserField>
            </div>
            <div className="rcf-pcell">
              <UserField tag={wide ? "跟进" : "跟"} wide={wide}>
                <Item name="followUpPerson">
                  <UserSelect allowCreate={false} placeholder="跟进人" />
                </Item>
              </UserField>
            </div>
          </div>
          <div className="rcf-divider" />
          <Row>
            <Cell f="contractStartDate">
              <Item name="contractStartDate" rules={req("开始日期")}>
                {dp("开始日期", "开始", false, () =>
                  goNext("contractStartDate"),
                )}
              </Item>
            </Cell>
            <Cell f="contractExpiryDate">
              <Item
                name="contractExpiryDate"
                dependencies={["contractStartDate"]}
                rules={[
                  ({ getFieldValue }) => ({
                    validator: (_, v) => {
                      const s = getFieldValue("contractStartDate");
                      return v && s && v.isBefore(s, "day")
                        ? Promise.reject(new Error("不能早于开始日期"))
                        : Promise.resolve();
                    },
                  }),
                ]}
              >
                {dp("结束日期（可不填）", "结束", true, () =>
                  goNext("contractExpiryDate"),
                )}
              </Item>
            </Cell>
          </Row>

          <Row>
            <Cell flex={0.9} f="rentPaymentCycle">
              <Item name="rentPaymentCycle" rules={req("付款类型")}>
                <Select
                  options={CYCLE_OPTIONS}
                  placeholder="付款类型"
                  onChange={() => goNext("rentPaymentCycle")}
                />
              </Item>
            </Cell>
            <Cell flex={0.9} f="rentPaymentCycleMultiplier">
              <Item name="rentPaymentCycleMultiplier" rules={req("付款周期")}>
                {num({
                  min: 1,
                  precision: 0,
                  prefix: "每",
                  addonAfter: unit || undefined,
                  placeholder: "周期",
                })}
              </Item>
            </Cell>
            <Cell flex={1.2} f="rent">
              <Item name="rent" rules={req("每次付费")}>
                {num({
                  min: 0,
                  precision: 2,
                  prefix: "租金¥",
                  placeholder: "每次付费",
                })}
              </Item>
            </Cell>
          </Row>
          <Row>
            <Cell flex={1} f="prePaidDays">
              <Item name="prePaidDays" rules={req("预付天数")}>
                {num({
                  min: 0,
                  precision: 0,
                  prefix: "预付",
                  addonAfter: "天",
                  placeholder: "预付天数",
                })}
              </Item>
            </Cell>
            <Cell flex={2} f="securityDeposit">
              <Item name="securityDeposit" rules={req("约定押金")}>
                {num({
                  min: 0,
                  precision: 2,
                  prefix: "押金¥",
                  placeholder: "约定押金",
                  // 编辑时：已付押金放在输入框尾部（绿=已付清 橙=部分 红=未付）
                  addonAfter: isEdit
                    ? (() => {
                        const paid = Number(contract.securityDepositPayed) || 0;
                        const dep = Number(contract.securityDeposit) || 0;
                        const color =
                          dep <= 0
                            ? "#94a3b8"
                            : paid >= dep
                              ? "#16a34a"
                              : paid > 0
                                ? "#d97706"
                                : "#dc2626";
                        return (
                          <span style={{ color, whiteSpace: "nowrap" }}>
                            已付 ¥{n2(paid)}
                          </span>
                        );
                      })()
                    : undefined,
                })}
              </Item>
            </Cell>
          </Row>

          <div className="rcf-divider" />
          <Row>
            <Cell>
              <Item name="freeWaterElectricity">
                <SideChip name="水电" />
              </Item>
            </Cell>
            <Cell>
              <Item name="freePropertyFee">
                <SideChip name="物业" disabled={cycle === "daily"} />
              </Item>
            </Cell>
          </Row>
          {(!freeWE || !freePF) && (
            <Row>
              <Cell>
                {!freeWE && (
                  <Field label="水电结算">
                    <Item
                      name="waterElectricityPaymentCycleMultiplier"
                      rules={req("水电结算周期")}
                    >
                      {num({
                        min: 1,
                        max: 2,
                        precision: 0,
                        prefix: "每",
                        addonAfter: "月",
                      })}
                    </Item>
                  </Field>
                )}
              </Cell>
              <Cell>
                {!freePF && (
                  <Field label="物业结算">
                    <Item
                      name="propertyFeePaymentCycleMultiplier"
                      rules={req("物业结算周期")}
                    >
                      {num({
                        min: 1,
                        max: 2,
                        precision: 0,
                        prefix: "每",
                        addonAfter: "月",
                      })}
                    </Item>
                  </Field>
                )}
              </Cell>
            </Row>
          )}
          {!freeWE && (
            <Row>
              <Cell f="waterMeterStart">
                <Item name="waterMeterStart">
                  {num({ prefix: "水表", placeholder: "水表底数" })}
                </Item>
              </Cell>
              <Cell f="electricityMeterStart">
                <Item name="electricityMeterStart">
                  {num({ prefix: "电表", placeholder: "电表底数" })}
                </Item>
              </Cell>
            </Row>
          )}

          <Row label="备注">
            <Cell f="notes">
              <Item name="notes">
                <Input.TextArea
                  autoSize={{ minRows: 2, maxRows: 4 }}
                  maxLength={300}
                />
              </Item>
            </Cell>
          </Row>
        </Form>
      </div>
    </Drawer>
  );
}
